/**
 * A trip whose end date has passed is a memory, not a workspace.
 *
 * Once past, the UI locks down every content-write path — no new
 * activities, ideas, polls, or packing items; no editing or deleting
 * existing ones; no voting on old polls; no claiming unclaimed
 * packing items.
 *
 * Expenses get a one-week grace period past the end date (see
 * isExpenseLoggingClosed below) so people can settle up their last
 * day's spending after getting home, before expenses lock down too.
 *
 * Still allowed on past trips (they belong in the post-trip flow):
 *   - viewing everything
 *   - the Recap
 *   - Settle-Up deep-link handoffs (people still owe each other)
 *   - Rename (a harmless retroactive correction)
 *   - Leave trip (member escape)
 *   - Delete trip (owner escape)
 *
 * Enforcement is client-side. Firestore rules could add an
 * `endDate < now()` gate later — this is a UX guarantee, not a
 * security one. Members can still write via the raw SDK if determined,
 * but the app never asks them to.
 */

import { getTripStatus, type Trip } from "@travelix/core";

/**
 * True when the trip has ended (getTripStatus → "past"). Undefined /
 * still-loading trip returns false so we don't flash a locked state
 * before we know the answer.
 */
export function isTripReadOnly(trip: Trip | undefined | null): boolean {
  if (!trip) return false;
  return getTripStatus(trip) === "past";
}

const EXPENSE_GRACE_PERIOD_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * True once the trip's expense grace period has elapsed — seven days
 * after the trip's end date. Adding, editing, and deleting expenses
 * stays open through the grace period even though the rest of the
 * trip (activities, ideas, polls, packing) already locked at
 * isTripReadOnly. Undefined / still-loading trip returns false so we
 * don't flash a locked state before we know the answer.
 */
export function isExpenseLoggingClosed(
  trip: Trip | undefined | null,
  nowMs: number = Date.now(),
): boolean {
  if (!trip) return false;
  return nowMs > trip.endDate.toMillis() + EXPENSE_GRACE_PERIOD_MS;
}
